import { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { Download, Globe, LayoutGrid, Loader2, X, ArrowUpDown, ArrowUp, ArrowDown, Cable, ChevronRight, AlertTriangle, BarChart3 } from 'lucide-react';
import { Card, CardContent } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Slider } from '@/shared/ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';
import { useInfiniteAccountServiceLines } from '@/domains/service-lines/services/account-service-lines';
import {
    getPreviousMonthDataUsage,
    getCurrentMonthDataUsage,
    getCurrentMonthUsagePercentage,
    getOverageInfo,
    getExhaustionInfoForServiceLine,
} from '@/shared/utils/serviceLineCalculations';
import { ServiceLine, OverageStatus, ServiceLineStatus, ServiceLineInfo } from '@/shared/types/serviceLineTypes';
import { formatDataAmount, formatDataUsage } from '@/shared/utils/format-utils';
import { Organization } from '@/shared/types/userTypes';
import EmptyView from '@/shared/components/EmptyView';
import { ServiceLineChartDialog } from '@/domains/service-lines/dialogs/ServiceLineChartDialog';
import { LocationMapDialog } from '@/domains/accounts/components/tabs/LocationMapDialog';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { formatDateToLocaleString } from '@/shared/utils/date-utils';
import { exportToCsv } from '@/shared/utils/csv-export-utils';
import { MarketDashboardDialog } from '@/domains/global-dashboard/dialogs/MarketDashboardDialog';
import SearchBar from '@/shared/components/SearchBar';
import { DEBOUNCE_TIME } from '@/shared/utils/input-validator';
import { OrganizationOverviewData } from '@/shared/types/overviewTypes';
import {
    getOverageStatusTextColorClass,
    getServiceLineStatusBGColorClass,
    SERVICE_LINE_STATUS_VALUE,
} from '@/shared/utils/color-utils-service-lines';
import { DashboardSummaryComponent } from '@/shared/components/DashboardSummaryComponent';

type SortField = 'usagePercentage';
type SortDirection = 'asc' | 'desc';

// Refetch dashboard data
export const refetchDashboardData = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: ['service-lines-infinite'] });
};

interface DashboardTabProps {
    organization: Organization;
    organizationOverviewData: OrganizationOverviewData | null | undefined;
    onManageServiceLine: (serviceLine: ServiceLine) => void;
}

export const DashboardTab = ({ organization, organizationOverviewData, onManageServiceLine }: DashboardTabProps) => {
    const [searchInput, setSearchInput] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState<string>('');

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setSearchQuery(searchInput.trim());
        }, DEBOUNCE_TIME);

        return () => window.clearTimeout(timeoutId);
    }, [searchInput]);

    const queryClient = useQueryClient();
    const {
        data: serviceLinesInfiniteData,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        error,
    } = useInfiniteAccountServiceLines(searchQuery);

    const serviceLines = useMemo(() => {
        const pages = serviceLinesInfiniteData?.pages ?? [];
        const seen = new Set<string>();
        const merged: ServiceLine[] = [];
        for (const page of pages) {
            for (const sl of page.results ?? []) {
                if (!seen.has(sl.serviceLineNumber)) {
                    seen.add(sl.serviceLineNumber);
                    merged.push(sl);
                }
            }
        }
        return merged;
    }, [serviceLinesInfiniteData?.pages]);

    const totalCount = serviceLinesInfiniteData?.pages[0]?.totalCount ?? 0;
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [dataConsumptionFilter, setDataConsumptionFilter] = useState<number[]>([0]);
    const [selectedServiceLine, setSelectedServiceLine] = useState<ServiceLine | null>(null);
    const [isChartDialogOpen, setIsChartDialogOpen] = useState<boolean>(false);
    const [isLocationMapDialogOpen, setIsLocationMapDialogOpen] = useState<boolean>(false);
    const [isMarketDashboardOpen, setIsMarketDashboardOpen] = useState<boolean>(false);

    // Sorting state
    const [sortField, setSortField] = useState<SortField>('usagePercentage');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    // Filter service lines based on current filters
    const filteredServiceLines = useMemo(() => {
        if (!serviceLines) return [];

        return serviceLines.filter(sl => {
            const statusText = SERVICE_LINE_STATUS_VALUE[sl.status];
            // Status filter
            if (statusFilter !== 'all' && statusText !== statusFilter) return false;

            // Data consumption filter
            const usagePercentage = getCurrentMonthUsagePercentage(sl);
            if (dataConsumptionFilter[0] !== 0 && usagePercentage !== null && usagePercentage < dataConsumptionFilter[0]) {
                return false;
            }

            return true;
        });
    }, [serviceLines, statusFilter, dataConsumptionFilter]);

    // Sort filtered service lines
    const sortedServiceLines = useMemo(() => {
        if (!filteredServiceLines) return [];

        return [...filteredServiceLines].sort((a, b) => {
            const aUsagePercentage = getCurrentMonthUsagePercentage(a) || 0;
            const bUsagePercentage = getCurrentMonthUsagePercentage(b) || 0;

            let comparison = 0;

            switch (sortField) {
                case 'usagePercentage':
                    comparison = aUsagePercentage - bUsagePercentage;
                    break;
                default:
                    comparison = 0;
            }

            return sortDirection === 'asc' ? comparison : -comparison;
        });
    }, [filteredServiceLines, sortField, sortDirection]);

    // Handle view chart
    const handleViewChart = (serviceLine: ServiceLine) => {
        setSelectedServiceLine(serviceLine);
        setIsChartDialogOpen(true);
    };

    // Handle close chart dialog
    const handleCloseChartDialog = () => {
        setIsChartDialogOpen(false);
        setSelectedServiceLine(null);
    };

    // Handle column sorting
    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc'); // Default to descending for new fields
        }
    };

    // Get sort icon for column
    const getSortIcon = (field: SortField) => {
        if (sortField !== field) {
            return <ArrowUpDown className="h-4 w-4" />;
        }
        return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
    };

    //Get current month service line info
    const getCurrentMonthServiceLineInfo = useCallback((serviceLine: ServiceLine): ServiceLineInfo => {
        const overageInfo = getOverageInfo(serviceLine.overage);
        const basePlan = serviceLine.product?.dataProduct?.dataAmount || 0;
        const previousMonthDataUsage = getPreviousMonthDataUsage(serviceLine.dataUsages)?.totalDataUsageGB || 0;
        const currentMonthDataUsage = getCurrentMonthDataUsage(serviceLine.dataUsages);
        const totalDataUsageGB = currentMonthDataUsage?.totalDataUsageGB || 0;
        const cappedForecast = currentMonthDataUsage?.cappedForecastGB || 0;
        const uncappedForecast = currentMonthDataUsage?.uncappedForecastGB || 0;
        const currentMonthOverageUsage = totalDataUsageGB < basePlan ? 0 : totalDataUsageGB - basePlan;
        const exhaustionInfo = getExhaustionInfoForServiceLine(serviceLine);
        const planAdequacy = exhaustionInfo?.planAdequacy;
        const exhaustionDate = planAdequacy === 'insufficient' ? formatDateToLocaleString(exhaustionInfo?.exhaustionDate.toISOString(), { month: 'short', day: 'numeric' }) : '-';
        const activationDate = serviceLine.startDate ? formatDateToLocaleString(serviceLine.startDate) : '-';
        const deactivationDate = serviceLine.endDate ? formatDateToLocaleString(serviceLine.endDate) : '-';
        const currentMonthBasePlanUsage = totalDataUsageGB < basePlan ? (totalDataUsageGB > 0 ? totalDataUsageGB : 0) : (basePlan > 0 ? basePlan : 0);
        const activeAlerts = serviceLine.terminals?.flatMap(terminal => terminal.activeAlerts || [])?.filter(alert => alert != null && alert.message).map(alert => alert.message?.split('_').join(' ').toUpperCase()) || [];
        return {
            serviceLineNumber: serviceLine.serviceLineNumber,
            serviceLineName: serviceLine.serviceLineName,
            activationDate: activationDate,
            deactivationDate: deactivationDate,
            customerName: serviceLine.customer?.name,
            status: serviceLine.status,
            productName: serviceLine.product?.orbiusProductName,
            terminals: serviceLine.terminals?.map(terminal => terminal.kitSerialNumber) || [],
            activeAlerts: activeAlerts,
            basePlan: basePlan,
            overageInfo: overageInfo,
            previousMonthDataUsage: previousMonthDataUsage,
            currentMonthBasePlanUsage: currentMonthBasePlanUsage,
            currentMonthOverageUsage: currentMonthOverageUsage,
            currentMonthTotalUsage: totalDataUsageGB,
            currentMonthUsagePercentage: getCurrentMonthUsagePercentage(serviceLine),
            currentMonthCappedForecast: cappedForecast,
            currentMonthUncappedForecast: uncappedForecast,
            currentMonthExhaustionDate: exhaustionDate,
        };
    }, []);
    // Handle export action
    const handleExportAction = () => {
        // If there is no data to export, show an alert
        if (!serviceLines || serviceLines.length === 0) {
            alert('No data to export');
            return;
        }

        // If the number of service lines is less than the total count, ask for confirmation
        if (serviceLines.length < totalCount) {
            if (confirm(`Only ${serviceLines.length} of ${totalCount} records will be exported. There are more records available on the next page(s). Please confirm to export?`)) {
                handleExport();
            }
            return;
        }

        // Export the data
        handleExport();
    }

    // Export the data
    const handleExport = () => {
        const csvData = serviceLines.map(serviceLine => {
            const serviceLineInfo = getCurrentMonthServiceLineInfo(serviceLine);
            return ({
                'Service Line Number': serviceLineInfo.serviceLineNumber || '-',
                'Service Line Name': serviceLineInfo.serviceLineName || '-',
                'Activation Date': serviceLineInfo.activationDate || '-',
                'Deactivation Date': serviceLineInfo.deactivationDate || '-',
                'Customer Name': serviceLineInfo.customerName || '-',
                'Status': SERVICE_LINE_STATUS_VALUE[serviceLineInfo.status] || '-',
                'Product Name': serviceLineInfo.productName || '-',
                'Terminal Number': serviceLineInfo.terminals?.join(', ') || '-',
                'Base Data Plan (GB)': formatDataAmount(serviceLineInfo.basePlan) || 0,
                'Overage Allowed': serviceLineInfo.overageInfo?.status === OverageStatus.ACTIVE ? 'Yes' : 'No',
                'Max Overage (GB)': formatDataAmount(serviceLineInfo.overageInfo?.dataAmount) || 0,
                'Previous Month Total Data Usage (GB)': formatDataAmount(serviceLineInfo.previousMonthDataUsage) || 0,
                'Current Month Base Plan Usage (GB)': formatDataAmount(serviceLineInfo.currentMonthBasePlanUsage) || 0,
                'Current Month Overage Usage (GB)': formatDataAmount(serviceLineInfo.currentMonthOverageUsage) || 0,
                'Current Month Total Usage (GB)': formatDataAmount(serviceLineInfo.currentMonthTotalUsage) || 0,
                'Current Month Usage Percentage(%)': serviceLineInfo.currentMonthUsagePercentage !== null ? serviceLineInfo.currentMonthUsagePercentage.toFixed(2) : '-',
                'Current Month\'s Capped Forecast (GB)*': formatDataAmount(serviceLineInfo.currentMonthCappedForecast) || 0,
                'Current Month\'s Uncapped Forecast (GB)*': formatDataAmount(serviceLineInfo.currentMonthUncappedForecast) || 0,
                'Current Month\'s Exhaustion Date*': serviceLineInfo.currentMonthExhaustionDate || '-',
            });
        });

        exportToCsv(csvData, `account_usage`);
    };

    return (
        <div className="space-y-4">
            {/* Dashboard Header */}
            <div className="p-4 flex items-center justify-between rounded-lg border border-border bg-muted">
                <div>
                    <h1 className="text-3xl font-bold">Dashboard</h1>
                    <p className="text-lg">Data consumption overview and service line monitoring</p>
                </div>
                <div className="flex items-center gap-2">
                    {organizationOverviewData && (
                        <>
                            {/* Market Dashboard */}
                            <Button variant="default" className="gap-2 bg-blue-500 hover:bg-blue-600" onClick={() => setIsMarketDashboardOpen(true)}>
                                <LayoutGrid className="h-4 w-4" />
                                Overview
                            </Button>
                        </>
                    )}
                    {serviceLines.length > 0 && (
                        <>
                            {/* Location Map */}
                            <Button variant="default" className="gap-2" onClick={() => {
                                setIsLocationMapDialogOpen(true);
                            }}>
                                <Globe className="h-4 w-4" />
                                Location Map
                            </Button>
                            {/* Export */}
                            {organizationOverviewData && <Button onClick={handleExportAction} className="gap-2">
                                <Download className="h-4 w-4" />
                                Export
                            </Button>
                            }
                        </>
                    )}
                </div>
            </div>

            {organizationOverviewData && serviceLines.length > 0 && (
                <DashboardSummaryComponent
                    organizationOverviewData={organizationOverviewData}
                    serviceLines={serviceLines}
                    mode={organization?.accountType === 'SUBSCRIPTION' ? 'subscription' : 'datapool'}
                />
            )}

            {/* Filters and Search */}
            <div className="glass-panel p-4 flex items-center gap-4 justify-between rounded-lg">
                {/* Search */}
                <div className="relative w-full max-w-1/2">
                    <SearchBar value={searchInput} onChange={setSearchInput} placeholder="Search service lines..." />
                </div>

                {/* Filters */}
                <div className="flex items-center gap-2 w-72">
                    <span className="text-sm font-medium">Data Usage(&ge;):</span>
                    <Slider
                        value={dataConsumptionFilter}
                        onValueChange={setDataConsumptionFilter}
                        max={100}
                        step={1}
                        className="w-32"
                    />
                    <span className="text-sm font-medium">{dataConsumptionFilter[0]}%</span>
                </div>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-56">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        {Object.values(ServiceLineStatus).map((status) => {
                            const statusText = SERVICE_LINE_STATUS_VALUE[status];
                            if (statusText) {
                                return (
                                    <SelectItem key={statusText} value={statusText}>
                                        <span className="flex items-center gap-2">{statusText}</span>
                                    </SelectItem>
                                );
                            }
                            return null;
                        })}
                    </SelectContent>
                </Select>

                <Button variant="outline" className="gap-2" onClick={() => {
                    setStatusFilter('all');
                    setDataConsumptionFilter([0]);
                    setSearchInput('');
                    setSearchQuery('');
                }}>
                    <X className="h-4 w-4" />
                    Clear Filters
                </Button>
            </div>

            {
                isLoading || (isFetching && !isFetchingNextPage) ? (
                    <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-8 w-8 animate-spin" />
                        <p className="text-sm">Loading service lines...</p>
                    </div>
                ) : (
                    <>
                        {/* Service Lines Table */}
                        {error ? (
                            <EmptyView
                                icon={Cable}
                                title="No Service Lines"
                                description="An error occurred while fetching service lines."
                                actionLabel="Retry"
                                onAction={() => refetchDashboardData(queryClient)}
                            />
                        ) : (serviceLines.length > 0 ? (
                            <>
                                <Card>
                                    <CardContent className="p-0">
                                        <div className="overflow-x-auto rounded-lg">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className="bg-muted">
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold">Service Line Name</TableHead>
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold">Status</TableHead>
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold">Customer</TableHead>
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold">Consumption</TableHead>
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold">Base Plan</TableHead>
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold">Overage Allowed</TableHead>
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold">Overage Incurred</TableHead>
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold cursor-pointer" onClick={() => handleSort('usagePercentage')}>
                                                            <span className="flex items-center gap-2">
                                                                Usage(%)
                                                                {getSortIcon('usagePercentage')}
                                                            </span>
                                                        </TableHead>
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold">Capped Forecast*</TableHead>
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold">Uncapped Forecast*</TableHead>
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold">Exhaustion Date*</TableHead>
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold">Historical Data</TableHead>
                                                        <TableHead className="text-left py-3 px-4 font-medium font-bold"></TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {sortedServiceLines.map((serviceLine) => {
                                                        const serviceLineInfo = getCurrentMonthServiceLineInfo(serviceLine);
                                                        const usagePercentage = serviceLineInfo.currentMonthUsagePercentage !== null ? serviceLineInfo.currentMonthUsagePercentage : null;
                                                        return (
                                                            <TableRow
                                                                key={serviceLine.serviceLineNumber}
                                                                className="border-b hover:bg-muted/50 cursor-pointer"
                                                                onClick={() => onManageServiceLine(serviceLine)}
                                                            >
                                                                <TableCell className="py-3 px-4 font-medium">
                                                                    <div className="flex flex-col items-start gap-1">
                                                                        <span className="text-sm font-medium">{serviceLine.serviceLineName}</span>
                                                                        <span className="text-xs">{serviceLine.serviceLineNumber}</span>
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell className="py-3 px-4">
                                                                    <div className="flex flex-col items-start gap-1">
                                                                        {serviceLineInfo.activeAlerts && serviceLineInfo.activeAlerts.length > 0 && serviceLineInfo.activeAlerts.some(alert => alert) && (
                                                                            <div className="flex flex-wrap gap-1">
                                                                                <TooltipProvider>
                                                                                    <Tooltip>
                                                                                        <TooltipTrigger asChild>
                                                                                            <div className="flex items-center gap-1">
                                                                                                <AlertTriangle className="h-5 w-5 text-amber-500" />
                                                                                                <span className="text-sm font-medium">{serviceLineInfo.activeAlerts.filter(alert => alert).length}</span>
                                                                                            </div>
                                                                                        </TooltipTrigger>
                                                                                        <TooltipContent className="max-w-xs uppercase bg-black text-white">
                                                                                            <ul>{serviceLineInfo.activeAlerts.filter(alert => alert).map((alert, index) => <li key={index}>{alert}</li>)}</ul>
                                                                                        </TooltipContent>
                                                                                    </Tooltip>
                                                                                </TooltipProvider>
                                                                            </div>
                                                                        )}

                                                                        <Badge className={getServiceLineStatusBGColorClass(serviceLineInfo.status)}>{SERVICE_LINE_STATUS_VALUE[serviceLineInfo.status]}</Badge>
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell className="py-3 px-4">{serviceLine.customer?.name || '-'}</TableCell>
                                                                <TableCell className="py-3 px-4">
                                                                    <div className="flex flex-col items-start gap-2">
                                                                        <span>{formatDataUsage(serviceLineInfo.currentMonthTotalUsage)}</span>
                                                                        <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                                            <div className={`h-full 
                                                                    ${usagePercentage > 95 ? 'bg-red-500' :
                                                                                    usagePercentage > 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                                                                                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell className="py-3 px-4">{formatDataUsage(serviceLineInfo.basePlan)}</TableCell>
                                                                <TableCell className={`py-3 px-4 ${getOverageStatusTextColorClass(serviceLineInfo.overageInfo?.status)} font-bold`}>{serviceLineInfo.overageInfo?.shortInfo}</TableCell>
                                                                <TableCell className="py-3 px-4">{formatDataAmount(serviceLineInfo.currentMonthOverageUsage) || 0}</TableCell>
                                                                <TableCell className="py-3 px-4">{usagePercentage !== null ? usagePercentage.toFixed(2) + '%' : 'N/A'}</TableCell>
                                                                <TableCell className="py-3 px-4">{formatDataUsage(serviceLineInfo.currentMonthCappedForecast)}</TableCell>
                                                                <TableCell className="py-3 px-4">{formatDataUsage(serviceLineInfo.currentMonthUncappedForecast)}</TableCell>
                                                                <TableCell className={`py-3 px-4 ${serviceLineInfo.currentMonthExhaustionDate == 'insufficient' ? 'font-bold' : ''}`}>{serviceLineInfo.currentMonthExhaustionDate}</TableCell>
                                                                <TableCell className="py-3 px-4">
                                                                    <Button
                                                                        variant="default"
                                                                        size="sm"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleViewChart(serviceLine)
                                                                        }}
                                                                        className="gap-2 bg-blue-500 hover:bg-blue-600"
                                                                    >
                                                                        <BarChart3 className="h-4 w-4" />
                                                                        View
                                                                    </Button>
                                                                </TableCell>
                                                                <TableCell className="py-3 px-4">
                                                                    <ChevronRight className="h-6 w-6" />
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                                <TableFooter>
                                                    <TableRow className="bg-muted">
                                                        <TableCell colSpan={13} className="py-3">
                                                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                                                <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                                                                    <div className="flex flex-col gap-2">
                                                                        <span className="text-md">
                                                                            Loaded <span className="font-medium">{serviceLines.length}</span>
                                                                            {' '}of <span className="font-bold">{totalCount}</span> service lines
                                                                        </span>
                                                                        {sortedServiceLines.length !== serviceLines.length && (
                                                                            <span className="text-sm">
                                                                                ({sortedServiceLines.length} match current search query or filter(s))
                                                                            </span>
                                                                        )}
                                                                        <p className="text-xs">* The monthly forecast is a linear projection based on average daily consumption this month.</p>
                                                                    </div>
                                                                </div>
                                                                {hasNextPage ? (
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="shrink-0 gap-2 self-start sm:self-auto"
                                                                        disabled={isFetchingNextPage}
                                                                        onClick={() => void fetchNextPage()}
                                                                    >
                                                                        {isFetchingNextPage ? (
                                                                            <>
                                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                                                Loading…
                                                                            </>
                                                                        ) : (
                                                                            'Load more'
                                                                        )}
                                                                    </Button>
                                                                ) : null}
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableFooter>
                                            </Table>
                                        </div>
                                    </CardContent>
                                </Card>
                            </>
                        ) : (
                            <EmptyView
                                icon={Cable}
                                title="No Service Lines"
                                description="This account doesn't have any service lines to display."
                            />
                        )
                        )}
                    </>
                )
            }

            {/* Service Line Chart Dialog */}
            <ServiceLineChartDialog
                serviceLine={selectedServiceLine}
                isOpen={isChartDialogOpen}
                onClose={handleCloseChartDialog}
            />

            <LocationMapDialog
                open={isLocationMapDialogOpen}
                onOpenChange={setIsLocationMapDialogOpen}
                onManageServiceLine={onManageServiceLine}
                serviceLines={sortedServiceLines}
            />

            {organizationOverviewData && (
                <MarketDashboardDialog
                    open={isMarketDashboardOpen}
                    onOpenChange={setIsMarketDashboardOpen}
                    organizationOverview={organizationOverviewData}
                />
            )}
        </div >
    );
}